/**
 * <responsive-video> — renders ONE of two viewport-specific videos.
 *
 * Wraps up to two <template> children (data-viewport="mobile" / "desktop"),
 * each holding the section's existing video markup (a <deferred-media> for
 * lazy-loaded videos, or a plain wrapper for eager ones, containing a
 * <video-component>). On connect it clones only the template that matches the
 * current viewport into the DOM, so just one video ever downloads / initialises.
 * On a real breakpoint crossing it tears the active one down and instantiates
 * the other, keeping the bytes-per-device promise and surviving Theme Editor
 * resizing / device emulation / orientation changes.
 *
 * Fallback contract (mirrors the section's image-desktop-and-mobile snippet,
 * but symmetric in both directions — see PR discussion):
 *   - both templates present  → correct one per viewport, swaps live on resize
 *   - only one template present → that one is used on ALL viewports
 * The breakpoint is supplied via data-desktop-media so it stays in lockstep
 * with the poster <picture> swap (min-width: 768px) defined in Liquid.
 *
 * Depends on the <video-component> / <deferred-media> custom elements. It does
 * NOT use pauseAllMedia(): that helper is module-scoped in main.js and, by
 * design, skips data-background="true" players — which is exactly what banner
 * videos are. We pause the underlying media directly on teardown instead.
 */
if (!customElements.get('responsive-video')) {
  class ResponsiveVideo extends HTMLElement {
    connectedCallback() {
      this.templates = {
        mobile: this.querySelector('template[data-viewport="mobile"]'),
        desktop: this.querySelector('template[data-viewport="desktop"]')
      };

      // Nothing to do without at least one template (defensive — Liquid only
      // renders the element when a video exists).
      if (!this.templates.mobile && !this.templates.desktop) return;

      this.controller = new AbortController();
      this.activeViewport = null;
      this.swapping = false;
      this.pendingRender = false;

      const desktopMedia = this.dataset.desktopMedia || '(min-width: 768px)';
      this.mql = window.matchMedia(desktopMedia);

      // Only watch for breakpoint changes when both videos exist; with a single
      // template the chosen viewport is fixed for every screen size.
      if (this.templates.mobile && this.templates.desktop) {
        this.mql.addEventListener('change', () => this.render(), {
          signal: this.controller.signal
        });
      }

      this.render();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      this.teardown();
    }

    /**
     * Resolves which viewport's template to use, honouring the single-template
     * fallback (one video plays everywhere).
     * @returns {('mobile'|'desktop')} The viewport to render.
     */
    resolveViewport() {
      if (!this.templates.desktop) return 'mobile';
      if (!this.templates.mobile) return 'desktop';
      return this.mql.matches ? 'desktop' : 'mobile';
    }

    /**
     * Renders the template for the current viewport if it isn't already active.
     * Guards against re-entry so a flurry of resize/change events can't stack
     * partially-built players.
     */
    render() {
      const next = this.resolveViewport();
      if (next === this.activeViewport) return;

      if (this.swapping) {
        this.pendingRender = true;
        return;
      }

      this.swapping = true;
      this.pendingRender = false;

      try {
        this.teardown();

        const fragment = this.templates[next].content.firstElementChild.cloneNode(true);
        this.appendChild(fragment);
        this.activeViewport = next;
      } finally {
        this.swapping = false;
      }

      if (this.pendingRender) {
        this.render();
      }
    }

    /**
     * Pauses and removes the currently-mounted video so no offscreen player
     * keeps buffering or playing audio after a swap. Pauses the underlying
     * media directly (HTML5 <video>, or a YouTube/Vimeo iframe via postMessage)
     * before detaching, then removes the wrapper so its DOM and observers are
     * garbage-collected.
     */
    teardown() {
      const mounted = this.querySelector('deferred-media, .responsive-video__eager');
      if (!mounted) return;

      mounted.querySelectorAll('video').forEach((video) => video.pause());
      mounted.querySelectorAll('.js-youtube').forEach((frame) => {
        frame.contentWindow.postMessage('{ "event": "command", "func": "pauseVideo", "args": "" }', '*');
      });
      mounted.querySelectorAll('.js-vimeo').forEach((frame) => {
        frame.contentWindow.postMessage('{ "method": "pause" }', '*');
      });

      mounted.remove();
      this.activeViewport = null;

      // Remove the played / autoplay-blocked state so the new viewport's
      // poster is visible until its own video actually starts playing (or is
      // refused in its own right — assets/video.js).
      const section = this.closest('.video-section');
      if (section) section.classList.remove('video-section--played', 'video-section--autoplay-blocked');
    }
  }

  customElements.define('responsive-video', ResponsiveVideo);
}
